import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Trash2, ShieldAlert, ShieldCheck, RefreshCw, Inbox as InboxIcon, Search, UserPlus, Send, Settings, MessageSquare } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Eyebrow, Spinner } from "@/components/tac";

const when = (s) => (s ? new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const LIMIT = 25;
const STAGES = ["new", "contacted", "quoted", "won", "lost"];
const SLABEL = { new: "New", contacted: "Contacted", quoted: "Quoted", won: "Won", lost: "Lost" };
const SCOLOR = { new: "bg-amber-500/15 text-amber-300", contacted: "bg-blue-500/15 text-blue-300", quoted: "bg-cyan-500/15 text-cyan-300", won: "bg-emerald-500/15 text-emerald-300", lost: "bg-slate-700/60 text-slate-400" };

export default function ContactInbox() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [includeSpam, setIncludeSpam] = useState(false);
  const [q, setQ] = useState("");
  const [term, setTerm] = useState("");
  const [stage, setStage] = useState("");
  const [team, setTeam] = useState([]);
  const [busyId, setBusyId] = useState("");
  const [replies, setReplies] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [ack, setAck] = useState({ ack_enabled: true, ack_template: "", default_template: "" });

  useEffect(() => {
    api.get("/auth/team").then(({ data }) => setTeam(Array.isArray(data) ? data : (data.members || data.items || []))).catch(() => setTeam([]));
    api.get("/workspace/reminder-settings").then(({ data }) => setAck(data)).catch(() => {});
  }, []);

  const [sp] = useSearchParams();
  useEffect(() => { const iq = sp.get("q"); if (iq) { setQ(iq); setTerm(iq); } }, [sp]);

  const fetchPage = useCallback(async (pg, append) => {
    try {
      const { data } = await api.get(`/contact/messages`, { params: { include_spam: includeSpam, q: term, stage, page: pg, limit: LIMIT } });
      setTotal(data.total); setHasMore(data.has_more); setPage(data.page); setCounts(data.counts || {});
      setItems((prev) => (append && prev ? [...prev, ...data.items] : data.items));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); if (!append) setItems([]); }
  }, [includeSpam, term, stage]);

  useEffect(() => { setItems(null); fetchPage(1, false); }, [fetchPage]);

  const patch = async (id, body, append) => {
    try { await api.patch(`/contact/messages/${id}`, body); if (!append) { setItems(null); fetchPage(1, false); } }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/contact/messages/${id}`); toast.success("Deleted"); setItems(null); fetchPage(1, false); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const convert = async (m) => {
    setBusyId(m.id);
    try {
      const { data } = await api.post(`/contact/messages/${m.id}/convert`, {});
      toast.success(data.already ? "Opening existing job" : "Customer + draft job created");
      nav(`/jobs/${data.job_id}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    setBusyId("");
  };
  const sendReply = async (id) => {
    const text = (replies[id] || "").trim();
    if (!text) return;
    try {
      await api.post(`/contact/messages/${id}/reply`, { body: text });
      setReplies((r) => ({ ...r, [id]: "" }));
      toast.success("Reply sent");
      setItems(null); fetchPage(1, false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try {
      await api.put(`/workspace/reminder-settings`, { ack_enabled: ack.ack_enabled, ack_template: ack.ack_template });
      toast.success("Auto-reply saved"); setShowSettings(false);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const teamName = (id) => (team.find((t) => t.id === id)?.name) || (team.find((t) => t.id === id)?.email) || "";

  const sel = "rounded-lg border border-slate-700 bg-[#0d1420] px-2 py-1 text-[11px] text-slate-200 focus:border-amber-500 focus:outline-none";

  return (
    <div className="space-y-6" data-testid="contact-inbox">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Eyebrow>Leads</Eyebrow>
          <h1 className="mt-1 font-display text-2xl font-bold text-white">Lead pipeline</h1>
          <p className="mt-1 text-sm text-slate-400">Website inquiries, tracked from new to won. Bot submissions are auto-filtered. {total} shown.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex cursor-pointer items-center gap-1.5 text-xs text-slate-400">
            <input data-testid="inbox-include-spam" type="checkbox" checked={includeSpam} onChange={(e) => setIncludeSpam(e.target.checked)} className="accent-amber-500" /> Show spam
          </label>
          <button data-testid="inbox-settings-btn" onClick={() => setShowSettings((s) => !s)} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-cyan-400"><Settings className="h-3.5 w-3.5" /> Auto-reply</button>
          <button data-testid="inbox-refresh" onClick={() => { setItems(null); fetchPage(1, false); }} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:border-amber-500"><RefreshCw className="h-3.5 w-3.5" /> Refresh</button>
        </div>
      </div>

      {showSettings && (
        <div className="card-tac space-y-3 p-4" data-testid="ack-settings">
          <div className="flex items-center gap-2 text-sm font-semibold text-white"><MessageSquare className="h-4 w-4 text-cyan-400" /> Automatic SMS reply</div>
          <p className="text-xs text-slate-400">Sent automatically when a customer texts back. Use <code className="text-cyan-300">{"{first_name}"}</code>, <code className="text-cyan-300">{"{company}"}</code> and <code className="text-cyan-300">{"{booking_link}"}</code> as placeholders.</p>
          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input data-testid="ack-enabled" type="checkbox" checked={!!ack.ack_enabled} onChange={(e) => setAck({ ...ack, ack_enabled: e.target.checked })} className="accent-amber-500" /> Send an automatic reply
          </label>
          <textarea data-testid="ack-template" value={ack.ack_template} onChange={(e) => setAck({ ...ack, ack_template: e.target.value })} rows={3} disabled={!ack.ack_enabled}
            className="w-full rounded-lg border border-slate-700 bg-[#0d1420] px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none disabled:opacity-50" />
          <div className="flex items-center gap-2">
            <button data-testid="ack-save" onClick={saveSettings} className="btn-amber rounded-full px-4 py-1.5 text-xs">Save auto-reply</button>
            {ack.default_template && <button onClick={() => setAck({ ...ack, ack_template: ack.default_template })} className="text-xs text-slate-500 hover:text-slate-300">Reset to default</button>}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="inbox-stage-filters">
        <button onClick={() => setStage("")} className={`rounded-full px-3 py-1.5 text-xs ${!stage ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`} data-testid="stage-filter-all">All</button>
        {STAGES.map((s) => (
          <button key={s} data-testid={`stage-filter-${s}`} onClick={() => setStage(s)} className={`rounded-full px-3 py-1.5 text-xs ${stage === s ? "bg-amber-500 text-black" : "border border-slate-700 text-slate-300"}`}>
            {SLABEL[s]} {counts[s] ? `(${counts[s]})` : ""}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        <div className="relative max-w-md flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input data-testid="inbox-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && setTerm(q)}
            placeholder="Search name, email, company or message…" className="w-full rounded-lg border border-slate-700 bg-[#0d1420] py-2 pl-9 pr-3 text-sm text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none" />
        </div>
        <button data-testid="inbox-search-btn" onClick={() => setTerm(q)} className="rounded-lg border border-slate-700 px-4 py-2 text-sm text-slate-200 hover:border-amber-500">Search</button>
        {term && <button onClick={() => { setQ(""); setTerm(""); }} className="text-xs text-slate-500 hover:text-slate-300">Clear</button>}
      </div>

      {items == null ? <div className="py-16"><Spinner label="Loading messages" /></div>
        : items.length === 0 ? (
          <div className="card-tac flex flex-col items-center gap-2 py-16 text-center text-slate-500" data-testid="inbox-empty">
            <InboxIcon className="h-8 w-8" /> {term || stage ? "No leads match this view." : "No messages yet."}
          </div>
        ) : (
          <div className="space-y-3">
            {items.map((m) => (
              <div key={m.id} data-testid={`inbox-msg-${m.id}`} className={`card-tac p-4 ${m.read ? "opacity-80" : ""}`}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-semibold text-white">{m.name}</span>
                      <span className={`rounded-full px-2 py-0.5 text-[10px] ${SCOLOR[m.stage] || SCOLOR.new}`} data-testid={`stage-badge-${m.id}`}>{SLABEL[m.stage] || "New"}</span>
                      <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">{m.topic || "General"}</span>
                      {m.spam && <span className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10px] text-rose-300">Spam</span>}
                      {m.converted && <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[10px] text-emerald-300">Converted</span>}
                      {!m.read && !m.spam && <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-300">New</span>}
                    </div>
                    <a href={`mailto:${m.email}`} className="mt-0.5 flex items-center gap-1 text-xs text-cyan-400 hover:underline"><Mail className="h-3 w-3" /> {m.email}{m.company ? ` · ${m.company}` : ""}</a>
                  </div>
                  <span className="font-mono-tac text-[10px] text-slate-500">{when(m.created_at)}</span>
                </div>
                <p className="mt-2 whitespace-pre-wrap text-sm text-slate-200">{m.message}</p>
                {((m.thread && m.thread.length) || m.phone) && (
                  <div className="mt-2 rounded-lg border border-slate-800 bg-[#0d1420] p-2" data-testid={`thread-${m.id}`}>
                    {m.thread && m.thread.length > 0 && (
                      <div className="space-y-1.5">
                        {m.thread.map((t, i) => (
                          <div key={i} className={`flex ${t.dir === "out" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-lg px-2.5 py-1.5 text-xs ${t.dir === "out" ? "bg-cyan-500/15 text-cyan-100" : "bg-slate-800 text-slate-200"}`}>
                              <div className="whitespace-pre-wrap">{t.body}</div>
                              <div className="mt-0.5 text-[9px] text-slate-500">{t.dir === "out" ? (t.auto ? "Auto-reply" : "You") : "Customer"} · {when(t.at)}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {m.phone && (
                      <div className="mt-2 flex items-center gap-2">
                        <input data-testid={`reply-input-${m.id}`} value={replies[m.id] || ""} onChange={(e) => setReplies({ ...replies, [m.id]: e.target.value })} onKeyDown={(e) => e.key === "Enter" && sendReply(m.id)}
                          placeholder={`Text ${m.phone}…`} className="flex-1 rounded-lg border border-slate-700 bg-[#0b101a] px-3 py-1.5 text-xs text-white placeholder:text-slate-600 focus:border-amber-500 focus:outline-none" />
                        <button data-testid={`reply-send-${m.id}`} onClick={() => sendReply(m.id)} className="btn-amber inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs"><Send className="h-3.5 w-3.5" /> Send SMS</button>
                      </div>
                    )}
                  </div>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <select data-testid={`stage-select-${m.id}`} value={m.stage || "new"} onChange={(e) => patch(m.id, { stage: e.target.value })} className={sel} title="Pipeline stage">
                    {STAGES.map((s) => <option key={s} value={s}>{SLABEL[s]}</option>)}
                  </select>
                  <select data-testid={`assign-select-${m.id}`} value={m.assigned_to || ""} onChange={(e) => patch(m.id, { assigned_to: e.target.value })} className={sel} title="Assign to">
                    <option value="">Unassigned</option>
                    {team.map((t) => <option key={t.id} value={t.id}>{t.name || t.email}</option>)}
                  </select>
                  {!m.converted && !m.spam && (
                    <button data-testid={`inbox-convert-${m.id}`} onClick={() => convert(m)} disabled={busyId === m.id} className="btn-amber inline-flex items-center gap-1 rounded-full px-3 py-1 text-[11px] disabled:opacity-50">
                      <UserPlus className="h-3 w-3" /> {busyId === m.id ? "Creating…" : "Convert to customer + job"}
                    </button>
                  )}
                  {m.converted && m.job_id && <button data-testid={`inbox-openjob-${m.id}`} onClick={() => nav(`/jobs/${m.job_id}`)} className="rounded-full border border-emerald-600/40 px-3 py-1 text-[11px] text-emerald-300">Open job</button>}
                  {!m.read && <button data-testid={`inbox-read-${m.id}`} onClick={() => patch(m.id, { read: true })} className="rounded-full border border-slate-700 px-3 py-1 text-[11px] text-slate-300 hover:border-emerald-500">Mark read</button>}
                  <button data-testid={`inbox-spam-${m.id}`} onClick={() => patch(m.id, { spam: !m.spam })} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-[11px] text-slate-300 hover:border-amber-500">
                    {m.spam ? <><ShieldCheck className="h-3 w-3" /> Not spam</> : <><ShieldAlert className="h-3 w-3" /> Mark spam</>}
                  </button>
                  <button data-testid={`inbox-delete-${m.id}`} onClick={() => del(m.id)} className="inline-flex items-center gap-1 rounded-full border border-slate-700 px-3 py-1 text-[11px] text-rose-300 hover:border-rose-500"><Trash2 className="h-3 w-3" /> Delete</button>
                  {m.assigned_to && <span className="text-[10px] text-slate-500">· {teamName(m.assigned_to)}</span>}
                </div>
              </div>
            ))}
            {hasMore && (
              <button data-testid="inbox-load-more" onClick={() => fetchPage(page + 1, true)} className="w-full rounded-lg border border-slate-700 py-2.5 text-sm text-slate-300 hover:border-amber-500">
                Load more ({items.length} of {total})
              </button>
            )}
          </div>
        )}
    </div>
  );
}
